import { RUNNING_SOLID } from './dashboardColors';
import type {
  IrrigationStatus, PeakHourModalState, StartFormType,
} from './riego.types';

// --- Gantt ---

export const STATUS_LABEL: Partial<Record<IrrigationStatus, string>> = {
  scheduled: 'Programado',
  proposal: 'Propuesta',
  running: 'En curso',
  executedOk: 'Regado',
  stoppedByUser: 'Detenido',
  failure: 'Falla',
};

export const LEGEND_ITEMS: Array<{
  status: IrrigationStatus; showBorder?: boolean; swatch?: string;
}> = [
  { status: 'scheduled', showBorder: true },
  { status: 'proposal', showBorder: true },
  { status: 'running', swatch: RUNNING_SOLID },
  { status: 'executedOk' },
  { status: 'stoppedByUser' },
  { status: 'failure' },
];

export const ROW_HEIGHT = 38;
export const CHART_HEADROOM = 70;
export const CHART_MIN_HEIGHT = 160;
export const POINT_PADDING = 0.18;
export const HOVER_BRIGHTNESS = 0.12;
export const TICK_PIXEL_INTERVAL = 90;
export const SWATCH_SIZE = 14;

export const MS_PER_MINUTE = 60 * 1000;
export const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const NOW_TICK_INTERVAL_MS = MS_PER_MINUTE;

export const SCHEDULED_STATUSES = new Set<IrrigationStatus>(['scheduled', 'proposal']);

export const RESUMABLE_STATUSES = new Set<IrrigationStatus>(['stoppedByUser', 'failure']);

// Real irrigations whose bar should split into an elapsed (fill) / remaining (track) progress,
// based on how far "now" is into their scheduled window.
export const PROGRESS_STATUSES = new Set<IrrigationStatus>(['running', 'stoppedByUser']);

// --- Irrigation control ---

export const MIN_DURATION_MIN = 1;
export const DEFAULT_DURATION_MIN = 30;
export const DEFAULT_PRECIPITATION_MM = 1.5;
export const INIT_TIME_LEAD_MIN = 5;
export const END_TIME_LEAD_MIN = 65;

export const START_TYPE_LABEL: Record<StartFormType, string> = {
  duration: 'Duración [min]',
  endTime: 'Fecha y Hora Fin',
  precipitation: 'Volumen [mm]',
};

export const START_TYPE_ORDER: StartFormType[] = ['endTime', 'precipitation', 'duration'];

// --- Peak hour modal ---

export const DEFAULT_INIT = '18:00';
export const DEFAULT_END = '23:00';
export const TIME_INPUT_STEP_SECONDS = 60;

// --- Dashboard ---

export const CLOSED_PEAK_HOUR_MODAL: PeakHourModalState = {
  open: false,
  pumpSystemId: null,
  pumpSystemName: '',
};

export const DATE_LABEL_MIN_WIDTH = 260;
export const QUERY_DEBOUNCE_MS = 450;
export const SKELETON_ROW_COUNT = 2;
export const SKELETON_LABEL_WIDTH = 220;
export const SKELETON_BLOCK_HEIGHT = 180;

// --- Layout ---

export const LATERAL_LIST_WIDTH = '290px';

// --- Schedule modal ---

export const ORPHAN_GROUP = 'Sin equipo';

// --- Irrigation program ---

export const BACKGROUND_COLORS = {
  PAST: '#f5f5f5',
  CURRENT: '#e3f2fd',
  FUTURE: '#ffffff',
} as const;

export const BORDER_STYLES = {
  VERTICAL_THIN: '1px solid rgba(224, 224, 224, 1)',
  VERTICAL_THICK: '2px solid rgba(224, 224, 224, 1)',
  HORIZONTAL_HEADER: '2px solid rgba(224, 224, 224, 1)',
  NONE: 'none',
} as const;

export const ROW_LABELS = {
  programmed: 'Riego Programado (mm)',
  real: 'Riego Real (mm)',
  cdtec: 'Riego CDTEC (mm)',
  difference: 'Diferencia CDTEC - Real (mm)',
  irrigationTime: 'Horas de riego real',
  theoreticalTime: 'Horas de riego teórica',
} as const;

// --- Irrigation detail panel ---

export const EM_DASH = '—';

// --- Stat chip ---

export const CHIP_PADDING_X = 1.75;
export const CHIP_PADDING_Y = 0.75;
export const CHIP_BORDER_RADIUS = 5;
export const CHIP_FONT_SIZE = 14;
